<div class="card h-100 d-flex flex-column">

    <!-- One branch for "there is a kørsel to show, or there is about to be". The header and the
         accordion container are mounted by this branch and stay mounted across a selection change —
         only their *contents* swap to skeletons. Unmounting them instead (the spinner this replaced)
         threw the pane's whole layout away on every click, which reads as a page change rather than
         a load, and took the folded/unfolded header with it.

         Note `request()` wins over `loading()`: a poll reloads the detail resource while its value
         is still there, and re-skeletoning content the user is reading would be a flicker for no
         information. Skeletons are for the empty wait only. -->
    @if (request() || loading()) {

    <!-- Title row stays put; the meta and progress below it fold away on short screens so the
         accordion gets the space. -->
    <div class="card-header bg-transparent flex-shrink-0">
        @if (request(); as detail) {
        <div class="d-flex align-items-start gap-2">
            <div class="flex-grow-1 min-w-0">
                <h5 class="mb-1 text-truncate">{{ synchronizationName() || detail.description }}</h5>
                <div class="small text-secondary text-truncate">
                    {{ customerName() || 'Kunde' }} [{{ detail.customerId }}] ·
                    {{ companyName() || 'Regnskab' }} [{{ detail.companyId }}]
                </div>
            </div>

            <!-- With the progress bar folded away the numbers would be lost, so they move up
                 here — outside the truncating title block, which would clip them. -->
            @if (!headerExpanded() && progress(); as folded) {
            <div class="small text-secondary tabular text-nowrap flex-shrink-0 align-self-center">
                {{ folded.done }}/{{ folded.total }} delkørsler
                @if (folded.failed > 0) {
                <span class="text-danger">· {{ folded.failed }} fejlet</span>
                }
            </div>
            }

            <div class="flex-shrink-0 d-flex align-items-center gap-2">
                <span class="badge" [class]="detail.status | queueStatusClass">
                    {{ detail.status | queueStatusLabel }}
                </span>
            </div>

            <button type="button" class="btn btn-sm btn-link text-secondary text-decoration-none p-1"
                aria-label="Kopiér link til denne kørsel" ngbTooltip="Kopiér link" placement="top" container="body"
                (click)="copyLink.emit()">
                <i class="fa-solid fa-link" aria-hidden="true"></i>
            </button>

            <!-- Tooltip text is static on purpose: ngbTooltip captures its content when it opens,
                 so a state-dependent string goes stale after a click. The chevron and the
                 aria-label carry the state instead. -->
            <button type="button" class="btn btn-sm btn-link text-secondary text-decoration-none p-1"
                [attr.aria-expanded]="headerExpanded()"
                [attr.aria-label]="headerExpanded() ? 'Skjul detaljer' : 'Vis detaljer'" ngbTooltip="Vis/skjul detaljer"
                placement="top" container="body" (click)="toggleHeader()">
                <i class="fa-solid" [class.fa-chevron-up]="headerExpanded()" [class.fa-chevron-down]="!headerExpanded()"
                    aria-hidden="true"></i>
            </button>
        </div>

        @if (headerExpanded()) {
        <dl class="detail-meta mb-0 mt-3">
            <dt>Skabelon</dt>
            <dd>
                <app-task-badge [action]="detail.action" [task]="detail.task" />
                @if (detail.templateName && detail.templateId) {
                <a [routerLink]="['/template', detail.templateId]" ngbTooltip="Åbn skabelonen"
                    placement="top" container="body">{{ detail.templateName }}</a>
                } @else {
                {{ detail.templateName ?? '—' }}
                }
            </dd>
            <dt>Beskrivelse</dt>
            <dd>{{ detail.description }}</dd>
            <dt>Startet</dt>
            <dd class="tabular">{{ (detail.created ?? detail.lastActivity) | date:'HH:mm dd-MM-yyyy' }}</dd>
            <dt>Køretid</dt>
            <dd class="tabular">{{ detail.runtime | formatRuntime }}</dd>
        </dl>

        <!-- Developer detail: where the time went. Admin-only — `timeline()` is empty for everyone
             else, so the block never renders for a user who just wants start, end and errors. -->
        @if (timeline().length > 0) {
        <ol class="timeline mt-3" aria-label="Tidslinje for kørslen">
            @for (item of timeline(); track item.start + item.kind) {
            <li class="timeline-item" [attr.data-kind]="item.kind">
                <span class="timeline-label">{{ item.label ?? (item.status | queueStatusLabel) }}</span>
                <span class="timeline-time tabular">{{ item.start | date:'HH:mm:ss' }}</span>
                @if (item.durationLabel) {
                <span class="timeline-duration tabular">{{ item.durationLabel }}</span>
                }
            </li>
            }
        </ol>
        }

        @if (progress(); as bar) {
        <div class="d-flex align-items-center gap-2 mt-3">
            <div class="progress flex-grow-1" role="progressbar" [attr.aria-valuenow]="bar.percent" aria-valuemin="0"
                aria-valuemax="100">
                <div class="progress-bar" [class.bar-failed]="bar.failed > 0" [style.width.%]="bar.percent"></div>
            </div>
            <span class="small text-nowrap tabular text-secondary">
                {{ bar.done }}/{{ bar.total }} delkørsler færdige
                @if (bar.failed > 0) {
                <span class="text-danger">· {{ bar.failed }} fejlet</span>
                }
            </span>
        </div>
        }
        }
        } @else {
        <!-- Skeleton header. Mirrors the real one line for line, and follows headerExpanded() so a
             folded pane does not unfold itself for the duration of the fetch. The whole block is
             aria-hidden; the one status line below carries the load for assistive tech. -->
        <div aria-hidden="true">
            <div class="d-flex align-items-start gap-2">
                <div class="flex-grow-1 min-w-0">
                    <div class="sk sk-title mb-2" style="width: 42%"></div>
                    <div class="sk sk-text" style="width: 28%"></div>
                </div>
                <div class="flex-shrink-0 d-flex align-items-center gap-2">
                    <div class="sk sk-badge" style="width: 5.5rem"></div>
                    <div class="sk sk-badge" style="width: 4.5rem"></div>
                </div>
                <div class="sk sk-icon"></div>
                <div class="sk sk-icon"></div>
            </div>

            @if (headerExpanded()) {
            <dl class="detail-meta mb-0 mt-3">
                <dt><span class="sk sk-text" style="width: 4.5rem"></span></dt>
                <dd><span class="sk sk-text" style="width: 55%"></span></dd>
                <dt><span class="sk sk-text" style="width: 5.5rem"></span></dt>
                <dd><span class="sk sk-text" style="width: 70%"></span></dd>
                <dt><span class="sk sk-text" style="width: 3.5rem"></span></dt>
                <dd><span class="sk sk-text" style="width: 40%"></span></dd>
                <dt><span class="sk sk-text" style="width: 4rem"></span></dt>
                <dd><span class="sk sk-text" style="width: 25%"></span></dd>
            </dl>

            <div class="d-flex align-items-center gap-2 mt-3">
                <div class="sk sk-progress flex-grow-1"></div>
                <div class="sk sk-text" style="width: 9rem"></div>
            </div>
            }
        </div>

        <span class="visually-hidden" role="status">Henter kørslen …</span>
        }
    </div>

    <div class="card-body p-0 flex-grow-1 overflow-hidden">
        <div class="accordion-scroll">
            @if (request(); as detail) {

            <!-- Kørsel-level log rows written before any chunk existed -->
            @if (leadingLogs().length > 0) {
            <div class="request-log-group">
                <div class="request-log-label">
                    <i class="fa-solid fa-diagram-project me-2" aria-hidden="true"></i>
                    Kørsel
                </div>
                @for (entry of leadingLogs(); track entry.id) {
                <app-queue-log-row [entry]="entry" variant="request" />
                }
            </div>
            }

            @if (detail.executers.length !== 0) {
            <app-executor-accordion [requestId]="detail.id" [executers]="detail.executers" [logs]="visibleLogs()"
                [developerInfo]="isAdmin" (downloadPayload)="downloadPayload.emit($event)" />
            }

            <!-- Kørsel-level log rows from the status rollup -->
            @if (trailingLogs().length > 0) {
            <div class="request-log-group">
                <div class="request-log-label">
                    <i class="fa-solid fa-flag-checkered me-2" aria-hidden="true"></i>
                    Afslutning
                </div>
                @for (entry of trailingLogs(); track entry.id) {
                <app-queue-log-row [entry]="entry" variant="request" />
                }
            </div>
            }

            } @else {
            <!-- Skeleton accordion: one status band and a handful of chunk rows, at the same indents
                 the real ones use, so the shape the content arrives into is already on screen. -->
            <div aria-hidden="true">
                <div class="sk-band">
                    <div class="sk" style="width: 7rem; height: 0.6875rem"></div>
                </div>
                @for (row of skeletonRows; track row) {
                <div class="sk-row">
                    <div class="sk sk-chevron"></div>
                    <div class="sk sk-text" style="width: 9rem"></div>
                    <div class="sk sk-text flex-grow-1"></div>
                    <div class="sk sk-text" style="width: 3.5rem"></div>
                </div>
                }
            </div>
            }
        </div>
    </div>

    } @else if (error(); as message) {
    <div class="card-body text-center py-5">
        <p class="text-danger mb-0">{{ message }}</p>
    </div>
    } @else {
    <div class="card-body text-center text-secondary py-5">
        Vælg en kørsel i listen.
    </div>
    }
</div>